import type {
  CreateLicenseRequest,
  LicenseDto,
  LicenseWithCode,
} from '@sellokart/shared';
import { api, queryString } from '@/lib/api';

export const licenseKeys = {
  /** Prefix for every licence query — invalidating this refreshes both views. */
  all: ['licenses'] as const,
  list: (tenantId?: string) => ['licenses', 'list', tenantId ?? 'all'] as const,
};

export function fetchLicenses(
  tenantId?: string,
  signal?: AbortSignal,
): Promise<LicenseDto[]> {
  return api.get<LicenseDto[]>(`/admin/licenses${queryString({ tenantId })}`, {
    signal,
  });
}

/**
 * The ONLY call that ever returns a plaintext activation code.
 *
 * Everything downstream must treat it as write-once: show it, let it be copied,
 * then drop it. The server keeps a SHA-256 hash and cannot re-issue it — if the
 * code is lost before the till activates, the licence has to be revoked and a
 * new one issued.
 */
export function issueLicense(
  tenantId: string,
  input: CreateLicenseRequest,
): Promise<LicenseWithCode> {
  return api.post<LicenseWithCode>(`/admin/tenants/${tenantId}/licenses`, input);
}

/** Adds months to the existing expiry, so no paid days are lost. */
export function extendLicense(id: string, months: number): Promise<LicenseDto> {
  return api.post<LicenseDto>(`/admin/licenses/${id}/extend`, { months });
}

/** Best-effort: it lands when the till next reaches the server. */
export function revokeLicense(id: string, reason: string): Promise<LicenseDto> {
  return api.post<LicenseDto>(`/admin/licenses/${id}/revoke`, { reason });
}

/**
 * Pause a licence without ending it. Reversible with [resumeLicense], and the
 * remaining term is kept — the shop resumes with the days it already had.
 */
export function suspendLicense(id: string, reason: string): Promise<LicenseDto> {
  return api.post<LicenseDto>(`/admin/licenses/${id}/suspend`, { reason });
}

/** Lifts a suspension. The till reopens on its next refresh, with no code. */
export function resumeLicense(id: string): Promise<LicenseDto> {
  return api.post<LicenseDto>(`/admin/licenses/${id}/resume`, {});
}

/**
 * Unbinds the hardware so the SAME licence can be activated on a replacement
 * till. Deliberately not a revocation: revoking would end a term the customer
 * has already paid for.
 */
export function deactivateLicense(
  id: string,
  reason: string,
): Promise<LicenseDto> {
  return api.post<LicenseDto>(`/admin/licenses/${id}/deactivate`, { reason });
}

/** True when a licence's term has run out, whatever the stored status says. */
export function isExpired(license: LicenseDto): boolean {
  return (
    !license.revokedAt &&
    license.expiresAt !== null &&
    new Date(license.expiresAt).getTime() < Date.now()
  );
}

/**
 * Anything worth a second look. Used for the fleet page's default sort and its
 * "Needs attention" filter, so both agree on what urgent means.
 */
export function needsAttention(license: LicenseDto): boolean {
  if (license.revokedAt) return false;
  // A pause is a deliberate state someone put it in, not a surprise — it
  // belongs on the fleet page as a status, not in "needs attention".
  if (license.suspendedAt) return false;
  if (license.cloneDetectedAt || license.clockAnomalyAt) return true;
  if (isExpired(license)) return true;
  // Active but silent: it should be refreshing hourly, so a week of nothing
  // means it cannot reach us — and it will expire with no warning delivered.
  if (license.status === 'ACTIVE' && license.lastRefreshAt) {
    const week = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - new Date(license.lastRefreshAt).getTime() > week;
  }
  return false;
}
